/**
 * Local persistence for the DRAFT planner — the one unfinished planner this device is building.
 *
 * A draft is a REAL backend group session (it has a share token, a 30-day TTL and guest-eater
 * ownership like any other); "draft" only describes its client-side state: started with zero
 * configuration and not yet shared. Keeping the pointer here (rather than in Redux) means it
 * survives a reload, a tab close and an app relaunch, which is what makes "resume an unfinished
 * draft" work — and it is what stops repeated "Plan with friends" taps from each minting a new
 * planner.
 */

import { safeLocalStorage } from './auth-helper'
import { errorLog } from '../utils/log-helper'

const DRAFT_KEY = 'rsly_planner_draft'

export interface PlannerDraft {
  /** The backend share token — this planner's identity everywhere else in the app. */
  shareToken: string
  /** Epoch ms the draft was started; powers the abandonment duration. */
  startedAt: number
  /** True once the user has shared the link at least once (an abandoned draft never was). */
  shared: boolean
}

/** The device's current unfinished planner, or null. Never throws. */
export const readPlannerDraft = (): PlannerDraft | null => {
  try {
    const raw = safeLocalStorage.getItem(DRAFT_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as Partial<PlannerDraft>
    if (!parsed || typeof parsed.shareToken !== 'string' || !parsed.shareToken) {
      return null
    }
    return {
      shareToken: parsed.shareToken,
      startedAt:
        typeof parsed.startedAt === 'number' ? parsed.startedAt : Date.now(),
      shared: parsed.shared === true,
    }
  } catch (e) {
    errorLog('[planner-draft] read failed', e)
    return null
  }
}

/** Persist (or replace) the device's draft pointer. */
export const writePlannerDraft = (draft: PlannerDraft): void => {
  try {
    safeLocalStorage.setItem(DRAFT_KEY, JSON.stringify(draft))
  } catch (e) {
    errorLog('[planner-draft] write failed', e)
  }
}

/**
 * Forget the draft pointer. Called when the planner graduates (shared and opened) or when the user
 * explicitly leaves planner mode — never deletes the backend session, which stays reachable via
 * "Your planners" and the share link.
 */
export const clearPlannerDraft = (): void => {
  try {
    safeLocalStorage.removeItem(DRAFT_KEY)
  } catch (e) {
    errorLog('[planner-draft] clear failed', e)
  }
}
